import { supabase } from '@/integrations/supabase/client';
import { SleepPlan, SleepPlanStatus } from '@/types/sleep-plan';

export class SleepPlanService {
  /**
   * Generate a new sleep plan for a child
   */
  static async generateSleepPlan(childId: string, contextData?: any): Promise<string> {
    console.log('🚀 SleepPlanService.generateSleepPlan called');
    console.log('  - Child ID:', childId);
    console.log('  - Context provided:', !!contextData);
    if (contextData) {
      console.log('  - Context keys:', Object.keys(contextData));
      console.log('  - Context values:', contextData);
    }

    const body: any = { child_id: childId };
    
    // Add context data if provided
    if (contextData) {
      body.context = contextData;
    }
    
    console.log('  - Invoking generate-sleep-plan edge function');
    
    const { data, error } = await supabase.functions.invoke('generate-sleep-plan', {
      body
    });

    if (error) {
      console.error('❌ Error invoking generate-sleep-plan function');
      console.error('  - Error:', error);
      console.error('  - Error message:', error.message);
      console.error('  - Full error object:', JSON.stringify(error, null, 2));
      throw new Error(error.message || 'Failed to generate sleep plan');
    }
    
    if (!data?.planId && !data?.plan_id) {
      console.error('❌ No plan ID returned from function');
      console.error('  - Response data:', data);
      throw new Error('No plan ID returned from server');
    }
    
    const planId = data.planId || data.plan_id;
    console.log('✅ Sleep plan generation initiated');
    console.log('  - Plan ID:', planId);
    
    return planId;
  }

  /**
   * Get a sleep plan by ID
   */
  static async getSleepPlan(planId: string): Promise<SleepPlan | null> {
    const { data, error } = await supabase
      .from('sleep_plans' as any)
      .select('*')
      .eq('id', planId)
      .single();

    if (error) {
      console.error('Error fetching sleep plan:', error);
      return null;
    }

    return data as unknown as SleepPlan;
  }

  /**
   * Get the latest active sleep plan for a child
   */
  static async getLatestSleepPlan(childId: string): Promise<SleepPlan | null> {
    const { data, error } = await supabase
      .from('sleep_plans' as any)
      .select('*')
      .eq('child_id', childId)
      .eq('is_active', true)
      .eq('generation_status', 'completed')
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No plan found
        return null;
      }
      console.error('Error fetching latest sleep plan:', error);
      return null;
    }

    return data as unknown as SleepPlan;
  }

  /**
   * Poll plan status until completed or failed
   */
  static async pollPlanStatus(
    planId: string,
    onStatusChange?: (status: SleepPlanStatus) => void,
    maxAttempts = 60,
    intervalMs = 2000
  ): Promise<SleepPlan> {
    let attempts = 0;

    return new Promise((resolve, reject) => {
      const poll = async () => {
        attempts++;

        const plan = await this.getSleepPlan(planId);

        if (!plan) {
          reject(new Error('Plan not found'));
          return;
        }

        onStatusChange?.(plan.generation_status);

        if (plan.generation_status === 'completed') {
          resolve(plan);
          return;
        }

        if (plan.generation_status === 'failed') {
          reject(new Error(plan.generation_error || 'Plan generation failed'));
          return;
        }

        if (attempts >= maxAttempts) {
          reject(new Error('Polling timeout'));
          return;
        }

        setTimeout(poll, intervalMs);
      };

      poll();
    });
  }

  /**
   * Get all plans for a child (history)
   */
  static async getChildPlanHistory(childId: string): Promise<SleepPlan[]> {
    const { data, error } = await supabase
      .from('sleep_plans' as any)
      .select('*')
      .eq('child_id', childId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching plan history:', error);
      return [];
    }

    return data as unknown as SleepPlan[];
  }

  /**
   * Deactivate old plans and mark new one as active
   */
  static async markPlanAsActive(planId: string, childId: string): Promise<void> {
    // Deactivate all plans for this child
    await supabase
      .from('sleep_plans' as any)
      .update({ is_active: false })
      .eq('child_id', childId);

    // Activate the new plan
    await supabase
      .from('sleep_plans' as any)
      .update({ is_active: true })
      .eq('id', planId);
  }
}
